import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Button, FilterDataSelect } from "@fullscreendigital/carrefour-dex-fe";
import { Modal } from "components/core-ui/Modal";
import { useFormik } from "formik";
import { getInitialValues } from "./helpers";
import { useNotification } from "hooks";
import { formatErrorMessage } from "utils/formatting";
import { CircularProgress, Grid } from "@mui/material";
import { FileUpload } from "components/core-ui/FileUpload";
import s from "components/core-ui/Modal/Modal.modules.scss";
import { makeCorePostRequest } from "utils/api";
import { useMutation } from "react-query";
import { useOfflineTool } from "components/core-ui/Offline";
import { AxiosError } from "axios";
import { getScreenPermissions } from "../../../api";
import { SCREENS } from "../../../constants";
import { AutocompleteOption } from "../../../../../volumetry/interfaces";

interface SGRProductsProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  shopTypes: AutocompleteOption[]
  shopCodes: AutocompleteOption[]
  isLoadingShopTypes: boolean
  isShopCodesLoading: boolean
}

const ImportProductsForm: React.FC<SGRProductsProps> = props => {
  const {
    isModalOpen,
    closeModal,
    setShouldRefreshData,
    shopTypes,
    shopCodes,
    isLoadingShopTypes,
    isShopCodesLoading
  } = props
  const { showNotification } = useNotification()
  const offlineTool = useOfflineTool()
  
  const [file, setFile] = useState<File | null>(null)

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_IMPORT_ALL_SHOP_TYPES)

  const onFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event === null) {
      formik.setFieldValue('file', null)
      return
    }

    const uploadedFile = event?.target.files && event?.target.files[0]

    if (uploadedFile) {
      const validFileType = uploadedFile.type === 'text/csv' || uploadedFile.name.endsWith('.csv')

      if (!validFileType) {
        showNotification({
          message: 'Fisierul este invalid!',
          type: 'error'
        })
        return
      }

      setFile(uploadedFile)
      formik.setFieldValue('file', uploadedFile)
    }
  }

  const importData = async (values: any) => {
    return await makeCorePostRequest(`/shop-reception/products/sgr/import`, values, {
      headers: {
        'Content-Type': 'multipart/form-data' // Ensure correct content type
      }
    });
  }

  const { isLoading, mutate } = useMutation(importData, {
    onSuccess: (resp: any) => {
      showNotification({
        message: resp?.result,
        type: 'success'
      })

      formik.resetForm()
      setShouldRefreshData(true)
      closeModal()
    },
    onError: (err: any) => {
      if (err?.code === AxiosError.ERR_NETWORK) {
        showNotification({
          message: 'Verificati conexiunea la internet',
          type: 'error'
        })

        // Store offline
        offlineTool.addOwnTask({
          type: `Produse cu SGR`,
          data: err.config.data as FormData,
          url: `/shop-reception/products/sgr/import`,
          method: 'post'
        })

        closeModal()

        return
      }
      showNotification({
        message: `A aparut o eroare. ${formatErrorMessage(err)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(shopCodes, shopTypes),
    [shopCodes, shopTypes]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    onSubmit: async (values: any) => {
      const newValues = { ...values, shopCodes: values.shopCodes.map(shopCode => shopCode.value) }

      const form = new FormData()

      if (newValues.shopCodes) form.append('shopCodes', newValues.shopCodes)
      if (newValues.ruleTypes && newValues.ruleTypes.length > 0) {
        const rules = newValues.ruleTypes.map(rule => rule.value)
        form.append("ruleTypes", rules)
      }

      form.append('file', file)

      mutate(form)
    }
  })

  useEffect(() => {
    if (isModalOpen) {
      formik.resetForm()
    }
  }, [isModalOpen])

  const handleSelectShopCodes = (shops: AutocompleteOption[]) => {
    formik.setFieldValue('shopCodes', shops)
  }

  const handleSelectRuleTypes = (rules: AutocompleteOption[]) => {
    formik.setFieldValue('ruleTypes', rules)
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={'Importă produse SGR'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid xs={12} item>
          {isShopCodesLoading ? (
            <CircularProgress color="primary" disableShrink />
          ) : (
            <FilterDataSelect
              key={JSON.stringify(formik.values.shopCodes)}
              name="shopCodes"
              label="Magazin"
              options={shopCodes}
              defaultSelectedData={formik.values.shopCodes}
              onSelect={handleSelectShopCodes}
              placeholderButton="Selecteaza"
              multiSelect={true}
              showSearch
            />
          )}
        </Grid>

        {(permissions?.canImportAllShopsAndAllShopTypes) && <Grid xs={12} item>
          {isLoadingShopTypes ? (
            <CircularProgress color="primary" disableShrink />
          ) : (
            <FilterDataSelect
              key={JSON.stringify(formik.values.ruleTypes)}
              name="ruleTypes"
              label="Regula"
              options={shopTypes}
              defaultSelectedData={formik.values.ruleTypes}
              onSelect={handleSelectRuleTypes}
              placeholderButton="Selecteaza"
              multiSelect={true}
              showSearch
            />
          )}
        </Grid>}

        <Grid xs={12} item>
          <FileUpload
            value={formik.values.file}
            fileInputProps={{
              displayImage: file
            }}
            onChange={onFileUpload}
          />
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading || isLoadingShopTypes || isShopCodesLoading || formik.values.file === null || (formik.values.shopCodes.length === 0 && formik.values.ruleTypes.length === 0)}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default ImportProductsForm
